import React, { useEffect, useMemo, useState } from 'react';
import { Sheet } from '../ui/Sheet.jsx';
import Button from '../ui/Button.jsx';
import Input from '../ui/Input.jsx';
import { useRecoveryEmailPrompt } from '../context/RecoveryEmailPromptContext.jsx';

function Benefit({ children }) {
  return (
    <div className="flex items-start gap-3 rounded-[22px] border border-ptcm-border/80 bg-ptcm-panel/45 px-4 py-3 dark:border-gray-800 dark:bg-gray-900/60">
      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ptcm-primary-soft text-ptcm-primary dark:bg-blue-900/40 dark:text-blue-300">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
          <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.25 7.25a1 1 0 01-1.415 0l-3-3a1 1 0 111.414-1.42l2.293 2.294 6.543-6.544a1 1 0 011.415 0z" clipRule="evenodd" />
        </svg>
      </div>
      <p className="text-sm text-ptcm-ink dark:text-gray-200">{children}</p>
    </div>
  );
}

export default function RecoveryEmailPromptSheet() {
  const {
    canRegisterRecoveryEmail,
    closePrompt,
    isOpen,
    isSubmitting,
    isPendingRecoveryEmailVerification,
    recoveryEmailStatus,
    savedEmail,
    submitError,
    submitRecoveryEmail
  } = useRecoveryEmailPrompt();

  const [email, setEmail] = useState('');
  const [successEmail, setSuccessEmail] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setSuccessEmail('');
      return;
    }

    setEmail(savedEmail || '');
    setSuccessEmail('');
  }, [isOpen, savedEmail]);

  const isSuccess = Boolean(successEmail);
  const isSubmitDisabled = isSubmitting || !String(email || '').trim();
  const title = useMemo(
    () => {
      if (isSuccess) return 'Verification envoyee';
      if (recoveryEmailStatus === 'pending') return 'Verifier mon email';
      return 'Enregistrer mon compte';
    },
    [isSuccess, recoveryEmailStatus]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      await submitRecoveryEmail({ email });
      setSuccessEmail(String(email || '').trim().toLowerCase());
    } catch (error) {
      // Error state is handled in context for display.
    }
  };

  if (!canRegisterRecoveryEmail && !isOpen) {
    return null;
  }

  return (
    <Sheet open={isOpen} onClose={closePrompt} title={title}>
      {isSuccess ? (
        <div className="space-y-5">
          <div className="rounded-[26px] border border-green-200/80 bg-green-50/90 px-4 py-4 text-green-800 dark:border-green-900/40 dark:bg-green-900/20 dark:text-green-200">
            <div className="text-sm font-semibold">Un lien de verification vient d'etre envoye.</div>
            <p className="mt-2 text-sm">
              Ouvrez votre boite mail puis cliquez sur le lien recu a <span className="font-semibold">{successEmail}</span> pour activer la reconnexion par email.
            </p>
          </div>

          <Button className="w-full" size="lg" onClick={closePrompt}>
            Fermer
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <div className="ptcm-section-pill w-fit">Sauvegarde</div>
            <p className="text-sm text-ptcm-muted dark:text-gray-300">
              Ajoutez votre email pour retrouver facilement ce questionnaire si vous changez d'appareil ou perdez votre code secret. Ce n'est pas obligatoire pour continuer.
            </p>
          </div>

          {isPendingRecoveryEmailVerification && savedEmail ? (
            <div className="rounded-[24px] border border-amber-200/80 bg-amber-50/90 px-4 py-4 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
              <div className="font-semibold">Verification en attente</div>
              <p className="mt-2">
                L'adresse <span className="font-semibold">{savedEmail}</span> est en attente de verification. Vous pouvez renvoyer un lien si besoin.
              </p>
            </div>
          ) : null}

          <div className="space-y-3">
            <Benefit>Reprendre votre progression sur ordinateur ou sur mobile.</Benefit>
            <Benefit>Retrouver votre code foyer et votre accès membre plus facilement.</Benefit>
            <Benefit>Recevoir ensuite un lien ou un email de reconnexion si vous perdez votre accès.</Benefit>
          </div>

          <div className="space-y-2">
            <label
              className="text-sm font-semibold text-ptcm-ink dark:text-gray-100"
              htmlFor="recovery-email"
            >
              Mon adresse email
            </label>
            <Input
              id="recovery-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="vous@exemple.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoFocus
            />
          </div>

          {submitError ? (
            <div className="rounded-2xl border border-red-200/70 bg-red-50/90 px-4 py-3 text-sm text-red-600 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
              {submitError}
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={closePrompt}>
              Plus tard
            </Button>
            <Button type="submit" disabled={isSubmitDisabled}>
              {isSubmitting ? 'Envoi...' : (recoveryEmailStatus === 'pending' ? 'Renvoyer le lien' : 'Enregistrer mon compte')}
            </Button>
          </div>
        </form>
      )}
    </Sheet>
  );
}
